import React, { useEffect, useMemo, useRef, useState } from "react";
import { onBackdropMouseDown, onModalPanelMouseDown } from "../lib/modal";
import { api, API_BASE } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { useBranch } from "@/context/BranchContext";
import { toast } from "sonner";
import { Plus, Minus, Trash2, ShoppingCart, MessageCircle, X, Printer, Search, CalendarCheck, Sparkles, Tag, Check, AlertCircle, Receipt, FileText, Scissors, Package as PackageIcon, Gift, Clock, User, Wallet, CreditCard, Star, Image as ImageIcon, Inbox, Building2 } from "lucide-react";
import { printInvoice } from "@/lib/printInvoice";
import { printPosReceipt } from "@/lib/printPosReceipt";
import { openCashDrawer, pairCashDrawer } from "@/lib/cashDrawer";
import { localizeError } from "@/lib/errors";
import LineDiscountEditor from "@/components/LineDiscountEditor";
import SplitPayModal from "@/components/SplitPayModal";
import TipSplitModal, { computeProportionalTipSplits } from "@/components/TipSplitModal";
import ConfirmDialog from "@/components/ConfirmDialog";
import PickPackageModal from "@/components/PickPackageModal";
import { selectedServiceLabel } from "@/lib/packageLines";

// Resolve uploaded asset URLs. Backend stores relative paths like
// `/api/uploads/general/xxx.png` — prepend the API base host. Absolute
// `http(s)://` URLs are returned untouched.
const resolveImg = (u) => {
  if (!u) return "";
  if (typeof u !== "string") return "";
  if (u.startsWith("http://") || u.startsWith("https://") || u.startsWith("data:")) return u;
  return API_BASE.replace(/\/api$/, "") + u;
};

export default function POS() {
  const { t, lang } = useLanguage();
  const { user } = useAuth();
  // POS is branch-aware: every invoice it creates is stamped (server-side)
  // with the branch_id that the active session is scoped to. We surface
  // that branch name in the header so the cashier always knows which
  // location they are operating in (multi-branch chains were getting
  // confused about which till they were ringing into).
  const { activeBranch, branches } = useBranch();
  const posBranchName = useMemo(() => {
    const b = activeBranch || (branches || []).find((x) => x.is_default) || (branches || [])[0];
    if (!b) return null;
    return lang === "ar"
      ? (b.name_ar || b.name_en || b.branch_id)
      : (b.name_en || b.name_ar || b.branch_id);
  }, [activeBranch, branches, lang]);
  const [tab, setTab] = useState("services");
  const [services, setServices] = useState([]);
  const [products, setProducts] = useState([]);
  const [packages, setPackages] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [accounts, setAccounts] = useState([]);
  const [paymentMethods, setPaymentMethods] = useState([]);
  const [staff, setStaff] = useState([]);  // Users who can be assigned to perform a cart line (drives commission attribution).
  const [cart, setCart] = useState([]);
  const [customerId, setCustomerId] = useState("");
  const [discount, setDiscount] = useState(0);
  const [taxRate, setTaxRate] = useState(0);
  // `method` now holds the method_id (not a type string). The invoice payload
  // serialises the underlying TYPE (cash/card/...) for backend compatibility.
  const [method, setMethod] = useState("");
  const [accountId, setAccountId] = useState("");
  const [redeemPoints, setRedeemPoints] = useState(0);
  const [couponCode, setCouponCode] = useState("");
  const [couponInfo, setCouponInfo] = useState(null); // { discount, type } when valid
  const [couponError, setCouponError] = useState("");
  const [couponChecking, setCouponChecking] = useState(false);
  const [custQuery, setCustQuery] = useState("");
  const [custDropOpen, setCustDropOpen] = useState(false);
  const [lineRecipientQuery, setLineRecipientQuery] = useState({});
  const [lineRecipientOpen, setLineRecipientOpen] = useState({});
  const [catalogQuery, setCatalogQuery] = useState("");
  // POS UX scale-up: when a salon has hundreds of services/products,
  // dumping the whole catalog into the grid is slow and overwhelms the
  // cashier. We now:
  //   • show a small category strip the cashier can click to drill in
  //   • paginate the grid (default page size = 12; "Show more" reveals
  //     the next page) — search bypasses pagination and returns matches.
  const [catalogCategory, setCatalogCategory] = useState(""); // "" = all
  const PAGE_SIZE = 12;
  const [visibleCount, setVisibleCount] = useState(PAGE_SIZE);
  const [recentInvoices, setRecentInvoices] = useState([]);
  const [recentSearch, setRecentSearch] = useState("");
  const [previewInvoice, setPreviewInvoice] = useState(null);
  const [previewPaymentSuccess, setPreviewPaymentSuccess] = useState(false);
  // Core Ops Wave A — link an in-progress POS sale back to a completed,
  // un-invoiced booking. Set by importFromBooking, cleared on cart reset.
  const [linkedBookingId, setLinkedBookingId] = useState(null);
  const custBoxRef = useRef(null);

  // ── Line-discount state (Option B) ──
  // policy.cashier_max_percent / cashier_max_amount drive when a manager
  // PIN is required; reasons[] feeds the picker. Editing one cart line
  // surfaces the LineDiscountEditor modal.
  const [discountReasons, setDiscountReasons] = useState([]);
  const [discountPolicy, setDiscountPolicy] = useState(null);
  const [editingDiscountIdx, setEditingDiscountIdx] = useState(null);
  const [splitPayOpen, setSplitPayOpen] = useState(false);
  const [checkoutBusy, setCheckoutBusy] = useState(false);
  const [staffPaySettings, setStaffPaySettings] = useState({ tips_enabled: true, require_staff_on_services: true, default_tip_presets: [10, 15, 20] });
  const [tipAmount, setTipAmount] = useState(0);
  const [tipSplits, setTipSplits] = useState([]);
  const [tipSplitOpen, setTipSplitOpen] = useState(false);
  const [bookingImportPrompt, setBookingImportPrompt] = useState(null);
  const [waStubPrompt, setWaStubPrompt] = useState(null);
  const [pickPackageTarget, setPickPackageTarget] = useState(null);
  const afterConsumptionRef = useRef(null);

  const isPickNPackage = (item) => {
    if (!item) return false;
    if (item.package_type === "pick_n") return true;
    const n = Number(item.pick_count || 0);
    return n > 0 && (item.service_ids || []).length >= n;
  };

  const resolvePackageItem = (item) => {
    const id = item?.package_id || item?.ref_id;
    return packages.find((p) => p.package_id === id) || item;
  };

  const lineNeedsConsumptionReview = (it) =>
    it?.kind === "package" && (it.selected_services || []).length > 0 && !it.consumption_reviewed;

  const findConsumptionReviewIndex = (items, after = -1) =>
    (items || []).findIndex((it, i) => i > after && lineNeedsConsumptionReview(it));

  const openConsumptionReviewAtIndex = (items, idx) => {
    const line = items[idx];
    if (!line) return;
    const pkg = resolvePackageItem(line);
    window.setTimeout(() => {
      setPickPackageTarget({
        pkg,
        cartIndex: idx,
        initialSelections: line.selected_services || [],
        selectionLocked: true,
        confirmLabel: lang === "ar" ? "متابعة للدفع" : "Continue to payment",
      });
    }, 0);
  };

  const openPickPackageForAdd = (item) => {
    const pkg = resolvePackageItem(item);
    if (!isPickNPackage(pkg)) {
      addToCart("package", pkg);
      return;
    }
    window.setTimeout(() => {
      setPickPackageTarget({
        pkg,
        cartIndex: null,
        initialSelections: [],
        selectionLocked: false,
      });
    }, 0);
  };

  const promptConsumptionReviews = (items, afterIdx = -1) => {
    const idx = findConsumptionReviewIndex(items, afterIdx);
    if (idx >= 0) openConsumptionReviewAtIndex(items, idx);
    return idx;
  };

  const loadRecent = async (search = "") => {
    try {
      const r = await api.get(`/invoices?limit=50${search ? `&search=${encodeURIComponent(search)}` : ""}`);
      // Show top 5 by default, or all matches when searching
      setRecentInvoices(search ? r.data : r.data.slice(0, 5));
    } catch (err) {
      console.error("[POS] recent load failed:", err);
    }
  };

  // Close customer dropdown on outside click
  useEffect(() => {
    const onDoc = (e) => {
      if (custBoxRef.current && !custBoxRef.current.contains(e.target)) setCustDropOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const load = async () => {
    // Every side endpoint is wrapped so the POS opens regardless of which
    // sibling pages the tenant's plan includes. The cashier still gets the
    // core service/product list (those are part of any plan that grants
    // POS access); the only consequence of a 403 here is the cashier won't
    // see hints from a sibling area (e.g. packages, accounts).
    const safe = (p) => p.catch(() => ({ data: [] }));
    const [s, p, pk, c, a, pm, u, dr, dp, sp] = await Promise.all([
      safe(api.get("/services")),
      safe(api.get("/products")),
      safe(api.get("/packages")),
      safe(api.get("/customers")),
      safe(api.get("/accounts")),
      safe(api.get("/payment-methods?active_only=true")),
      safe(api.get("/staff-assignable")),
      safe(api.get("/discount-reasons")),
      safe(api.get("/pos/discount-policy")),
      safe(api.get("/settings/staff-pay")),
    ]);
    setServices(s.data); setProducts(p.data); setPackages(pk.data); setCustomers(c.data); setAccounts(a.data);
    // Staff: everyone except admins. Each cart line lets the cashier pick who performed the work.
    setStaff((u.data || []).filter((x) => x.role !== "admin"));
    setPaymentMethods(Array.isArray(pm.data) ? pm.data : []);
    setDiscountReasons(Array.isArray(dr.data) ? dr.data : []);
    setDiscountPolicy(dp.data && !Array.isArray(dp.data) ? dp.data : null);
    if (sp.data && typeof sp.data === "object") setStaffPaySettings(sp.data);

    // Resolve the initial payment method. The salon-set default (is_default
    // flag on the payment method itself) is the anchor and wins. Per-user
    // preference is only a fallback so individual cashiers can have their own
    // method without forcing every operator into the same starting state.
    //   1. The payment method flagged is_default=true at the salon level.
    //   2. The user's default_payment_method (matched by type).
    //   3. The first available method.
    let chosen = (pm.data || []).find(m => m.is_default) || null;
    if (!chosen && user?.default_payment_method) {
      chosen = (pm.data || []).find(m => m.type === user.default_payment_method) || null;
    }
    if (!chosen) chosen = (pm.data || [])[0] || null;
    if (chosen) setMethod(chosen.method_id);

    // Resolve the cashbox/account. The cashbox follows the selected payment
    // method so the cashier sees "default method + its linked account" as a
    // single, coherent default. Fallbacks kick in only when the method has
    // no explicit link.
    //   1. selected method's target_account_id.
    //   2. account flagged is_default=true at the salon level.
    //   3. user's default_account_id.
    //   4. the first available account.
    let chosenAccount = "";
    if (chosen?.target_account_id && (a.data || []).some(x => x.account_id === chosen.target_account_id)) {
      chosenAccount = chosen.target_account_id;
    } else {
      const flagged = (a.data || []).find(x => x.is_default);
      if (flagged) {
        chosenAccount = flagged.account_id;
      } else if (user?.default_account_id && (a.data || []).some(x => x.account_id === user.default_account_id)) {
        chosenAccount = user.default_account_id;
      } else if ((a.data || []).length > 0) {
        chosenAccount = a.data[0].account_id;
      }
    }
    if (chosenAccount) setAccountId(chosenAccount);
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [user?.default_payment_method, user?.default_account_id]);

  // Refresh payment config when the cashier returns to this tab — they may
  // have added methods/accounts in Settings without a full page reload.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") {
        api.get("/payment-methods?active_only=true")
          .then((r) => setPaymentMethods(Array.isArray(r.data) ? r.data : []))
          .catch(() => {});
        api.get("/accounts")
          .then((r) => setAccounts(Array.isArray(r.data) ? r.data : []))
          .catch(() => {});
      }
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  // Whenever the cashier toggles the payment method, auto-route the sale into
  // the linked cashbox/bank account defined on the payment method itself. The
  // cashier can still override manually afterwards; we never overwrite a
  // selection they explicitly changed during the same sale.
  const onMethodChange = (methodId) => {
    setMethod(methodId);
    const m = paymentMethods.find(x => x.method_id === methodId);
    if (m?.target_account_id && accounts.some(x => x.account_id === m.target_account_id)) {
      setAccountId(m.target_account_id);
    }
  };

  const findCustomerByQuery = (query) => {
    const q = normalizeCustomerSearch(query);
    if (!q || q.length < 2) return null;
    const matches = customers.filter((c) => matchesCustomerSearch(c, q));
    if (matches.length !== 1) return null;
    return matches[0] || null;
  };

  const addToCart = (kind, item) => {
    const id = item.service_id || item.product_id || item.package_id;
    const name = lang === "ar" ? item.name_ar : item.name_en;
    const price = kind === "product" && item.sale_price != null ? item.sale_price : item.price;
    const image_url = item.image_url || "";
    // If the logged-in user is staff (not admin/manager), pre-assign them as
    // the performer so the most common single-stylist sale needs no extra clicks.
    const defaultEmp = (user && user.role === "staff") ? user.user_id : null;
    setCart(c => {
      const idx = c.findIndex(x => x.kind === kind && x.ref_id === id && (x.employee_user_id || null) === defaultEmp && !(x.selected_services || []).length);
      if (idx >= 0) { const n = [...c]; n[idx].qty += 1; return n; }
      return [...c, {
        kind,
        ref_id: id,
        name,
        unit_price: price,
        qty: 1,
        image_url,
        employee_user_id: defaultEmp,
        recipient_customer_id: null,
        recipient_customer_name: null,
        recipient_customer_phone: null,
        paid_by_customer_id: customerId || null,
        paid_by_customer_name: selectedCust?.name || null,
        paid_by_customer_phone: selectedCust?.phone || null,
      }];
    });
  };

  const addPickPackageToCart = (pkg, selected_services) => {
    const name = lang === "ar" ? pkg.name_ar : pkg.name_en;
    setCart((c) => [...c, {
      kind: "package",
      ref_id: pkg.package_id,
      name,
      unit_price: pkg.price,
      qty: 1,
      image_url: pkg.image_url || "",
      employee_user_id: null,
      selected_services,
      consumption_reviewed: true,
      package_type: pkg.package_type || "pick_n",
      recipient_customer_id: null,
      recipient_customer_name: null,
      recipient_customer_phone: null,
      paid_by_customer_id: customerId || null,
      paid_by_customer_name: selectedCust?.name || null,
      paid_by_customer_phone: selectedCust?.phone || null,
    }]);
    setPickPackageTarget(null);
  };

  const handlePickPackageConfirm = (selected_services) => {
    const target = pickPackageTarget;
    if (!target?.pkg) return;
    setPickPackageTarget(null);

    if (target.cartIndex != null) {
      setCart((c) => {
        const updated = c.map((line, i) => (i === target.cartIndex
          ? { ...line, selected_services, consumption_reviewed: true }
          : line));
        window.setTimeout(() => {
          const nextIdx = findConsumptionReviewIndex(updated, target.cartIndex);
          if (nextIdx >= 0) {
            openConsumptionReviewAtIndex(updated, nextIdx);
          } else if (afterConsumptionRef.current) {
            const fn = afterConsumptionRef.current;
            afterConsumptionRef.current = null;
            fn();
          }
        }, 0);
        return updated;
      });
      return;
    }

    addPickPackageToCart(target.pkg, selected_services);
  };

  const handleCatalogAdd = (kind, item) => {
    if (kind === "package" && isPickNPackage(resolvePackageItem(item))) {
      openPickPackageForAdd(item);
      return;
    }
    addToCart(kind, item);
  };

  const runCheckoutWithConsumptionGate = (doCheckout) => {
    if (!validateBeforeCheckout()) return;
    const idx = findConsumptionReviewIndex(cart);
    if (idx >= 0) {
      afterConsumptionRef.current = doCheckout;
      openConsumptionReviewAtIndex(cart, idx);
      return;
    }
    doCheckout();
  };

  const updateQty = (i, d) => setCart(c => c.map((x, idx) => idx === i ? { ...x, qty: Math.max(1, x.qty + d) } : x));
  const removeItem = (i) => setCart(c => c.filter((_, idx) => idx !== i));
  // Attach (or detach when empty) the staff member who performed this line.
  // Drives commission attribution on the resulting invoice.
  const setLineEmployee = (i, userId) => setCart(c => c.map((x, idx) => idx === i ? { ...x, employee_user_id: userId || null } : x));
  const setLineRecipient = (i, cust) => setCart(c => c.map((x, idx) => idx === i ? {
    ...x,
    recipient_customer_id: cust?.customer_id || null,
    recipient_customer_name: cust?.name || null,
    recipient_customer_phone: cust?.phone || null,
  } : x));

  const subtotal = useMemo(() => cart.reduce((s, i) => s + i.unit_price * i.qty, 0), [cart]);
  // Net subtotal after line-level discounts — used in totals row.
  const lineDiscountTotal = useMemo(() => cart.reduce((s, i) => {
    if (!i.discount_type || i.discount_type === "none" || !i.discount_value) return s;
    const ls = i.unit_price * i.qty;
    if (i.discount_type === "percent") return s + Math.min(i.discount_value, 100) / 100 * ls;
    return s + Math.min(i.discount_value, ls);
  }, 0), [cart]);
  const subtotalAfterLine = Math.max(0, subtotal - lineDiscountTotal);
  const selectedCust = customers.find(c => c.customer_id === customerId);
  const maxRedeem = selectedCust?.loyalty_points || 0;
  const redeemValue = Math.min(parseInt(redeemPoints) || 0, maxRedeem) / 30;
  const couponDiscount = couponInfo ? Number(couponInfo.discount || 0) : 0;
  const afterDisc = Math.max(0, subtotalAfterLine - discount - couponDiscount - redeemValue);
  const tax = +(afterDisc * (taxRate / 100)).toFixed(2);
  const total = +(afterDisc + tax).toFixed(2);
  const tipTotal = +(Math.max(0, parseFloat(tipAmount) || 0)).toFixed(2);
  const amountDue = +(total + tipTotal).toFixed(2);

  const applyTipPercent = (pct) => {
    const amt = +(total * (pct / 100)).toFixed(2);
    setTipAmount(amt);
    setTipSplits(computeProportionalTipSplits(cart, amt));
  };

  const validateBeforeCheckout = () => {
    if (cart.length === 0) {
      toast.error(lang === "ar" ? "السلة فارغة" : "Empty cart");
      return false;
    }
    if (staffPaySettings.require_staff_on_services !== false) {
      const missing = cart.filter((it) => it.kind === "service" && !it.employee_user_id);
      if (missing.length) {
        toast.error(lang === "ar" ? "حدد الموظف لكل خدمة قبل الدفع" : "Assign staff to each service before checkout");
        return false;
      }
      for (const it of cart) {
        if (it.kind !== "package" || !(it.selected_services || []).length) continue;
        const missingStaff = it.selected_services.filter((s) => !s.employee_user_id);
        if (missingStaff.length) {
          toast.error(lang === "ar" ? "حدد الموظف لكل خدمة داخل الباقة" : "Assign staff to each service inside the package");
          return false;
        }
      }
    }
    for (const it of cart) {
      if (it.kind !== "package") continue;
      const pkg = resolvePackageItem(it);
      if (!isPickNPackage(pkg)) continue;
      const need = Number(pkg.pick_count || 0);
      const got = (it.selected_services || []).length;
      if (got !== need) {
        toast.error(lang === "ar" ? `الباقة "${it.name}" تحتاج ${need} خدمات محددة` : `Package "${it.name}" needs ${need} selected service(s)`);
        return false;
      }
    }
    if (tipTotal > 0) {
      if (!tipSplits.length) {
        toast.error(lang === "ar" ? "وزّع الإكرامية على الموظفين" : "Distribute the tip among staff");
        setTipSplitOpen(true);
        return false;
      }
      const splitSum = tipSplits.reduce((s, x) => s + Number(x.amount || 0), 0);
      if (Math.abs(splitSum - tipTotal) > 0.02) {
        toast.error(lang === "ar" ? "مجموع توزيع الإكرامية غير صحيح" : "Tip split does not match tip amount");
        setTipSplitOpen(true);
        return false;
      }
    }
    return true;
  };

  // Re-validate or clear coupon when cart total / customer changes (auto invalidate stale validations)
  useEffect(() => {
    if (couponInfo) {
      // If cart goes below min_invoice_amount, clear silently. Otherwise re-validate to recompute discount.
      const code = couponInfo.code || couponCode;
      if (!code || subtotal === 0) { setCouponInfo(null); return; }
      // Simple recomputation for percent / fixed types — no extra round-trip.
      const t = couponInfo.type;
      let d = 0;
      if (t === "percent" || t === "first_booking") d = subtotal * (Number(couponInfo.value || 0) / 100);
      else if (t === "fixed") d = Math.min(Number(couponInfo.value || 0), subtotal);
      if (Math.abs(d - couponDiscount) > 0.01) {
        setCouponInfo(prev => prev ? { ...prev, discount: +d.toFixed(2) } : prev);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [subtotal, customerId]);

  const applyCoupon = async () => {
    const code = couponCode.trim().toUpperCase();
    setCouponError("");
    if (!code) { setCouponInfo(null); return; }
    if (subtotal <= 0) {
      setCouponError(lang === "ar" ? "أضف منتجات أولاً" : "Add items first");
      return;
    }
    setCouponChecking(true);
    try {
      const r = await api.post("/coupons/validate", {
        code,
        invoice_amount: subtotal,
        customer_id: customerId || null,
      });
      // Persist value/type for client-side recompute when cart changes
      setCouponInfo({ code, discount: r.data.discount, type: r.data.type, value: r.data.value, coupon_id: r.data.coupon_id });
      toast.success(lang === "ar" ? `تم تطبيق الكوبون: خصم ${r.data.discount.toFixed(2)} ر.ق` : `Coupon applied: ${r.data.discount.toFixed(2)} QAR off`);
    } catch (e) {
      setCouponInfo(null);
      const msg = e.response?.data?.detail || (lang === "ar" ? "كود غير صالح" : "Invalid code");
      setCouponError(msg);
    } finally {
      setCouponChecking(false);
    }
  };

  const clearCoupon = () => {
    setCouponInfo(null);
    setCouponCode("");
    setCouponError("");
  };

  const buildInvoicePayload = (paymentExtra = {}) => {
    const cust = selectedCust;
    return {
      customer_id: customerId || null,
      customer_name: cust?.name || "Walk-in",
      customer_phone: cust?.phone || "",
      items: cart.map((it) => {
        const {
          _discount_manager_pin,
          consumption_reviewed,
          package_type,
          image_url,
          ...rest
        } = it;
        const row = _discount_manager_pin
          ? { ...rest, discount_manager_pin: _discount_manager_pin }
          : rest;
        return row;
      }),
      discount: parseFloat(discount) || 0,
      tax_rate: parseFloat(taxRate) || 0,
      loyalty_redeemed: Math.min(parseInt(redeemPoints) || 0, maxRedeem),
      coupon_code: couponInfo ? (couponInfo.code || couponCode) : "",
      booking_id: linkedBookingId || null,
      tip_total: tipTotal,
      tip_splits: tipTotal > 0 ? tipSplits : [],
      ...paymentExtra,
    };
  };

  const resetCheckoutState = () => {
    setCart([]);
    setDiscount(0);
    setRedeemPoints(0);
    clearCoupon();
    setLinkedBookingId(null);
    setSplitPayOpen(false);
    setTipAmount(0);
    setTipSplits([]);
  };

  const finalizeCheckout = async (payload) => {
    setCheckoutBusy(true);
    try {
      const r = await api.post("/invoices", payload);
      setPreviewInvoice(r.data);
      setPreviewPaymentSuccess(true);
      resetCheckoutState();
      load();
    } catch (e) {
      toast.error(localizeError(e, t, lang));
    } finally {
      setCheckoutBusy(false);
    }
  };

  const checkout = async () => {
    runCheckoutWithConsumptionGate(async () => {
      if (!accountId) {
        toast.error(lang === "ar" ? "الرجاء اختيار صندوق/بنك للدفع" : "Please select a Cash/Bank account");
        return;
      }
      const selectedMethod = paymentMethods.find(m => m.method_id === method);
      const methodType = selectedMethod?.type || "cash";
      await finalizeCheckout(
        buildInvoicePayload({ payment_method: methodType, account_id: accountId }),
      );
    });
  };

  const checkoutSplit = async (splits) => {
    runCheckoutWithConsumptionGate(async () => {
      await finalizeCheckout(
        buildInvoicePayload({ payment_splits: splits }),
      );
    });
  };

  const formatPaymentLabel = (inv) => {
    if (inv?.payment_splits?.length) {
      const labels = inv.payment_splits.map((s) => `${s.payment_method} ${Number(s.amount).toFixed(2)}`);
      return lang === "ar" ? `مقسّم: ${labels.join(" + ")}` : `Split: ${labels.join(" + ")}`;
    }
    return inv?.payment_method || "";
  };

  const refreshPaymentConfig = async () => {
    try {
      const [pm, a] = await Promise.all([
        api.get("/payment-methods?active_only=true"),
        api.get("/accounts"),
      ]);
      const methods = Array.isArray(pm.data) ? pm.data : [];
      const accts = Array.isArray(a.data) ? a.data : [];
      setPaymentMethods(methods);
      setAccounts(accts);
      return { methods, accts };
    } catch {
      return { methods: paymentMethods, accts: accounts };
    }
  };

  const openSplitPay = async () => {
    if (cart.length === 0) {
      toast.error(lang === "ar" ? "السلة فارغة" : "Empty cart");
      return;
    }
    runCheckoutWithConsumptionGate(async () => {
      const { methods, accts } = await refreshPaymentConfig();
      if (methods.length < 2) {
        try {
          const allRes = await api.get("/payment-methods");
          const allMethods = Array.isArray(allRes.data) ? allRes.data : [];
          const inactiveCount = allMethods.filter((m) => m.is_active === false).length;
          if (allMethods.length >= 2 && methods.length < 2) {
            toast.error(
              lang === "ar"
                ? `لديك ${allMethods.length} طرق دفع لكن ${inactiveCount} غير نشطة. فعّلها من الحسابات → طرق الدفع ثم أعد المحاولة.`
                : `You have ${allMethods.length} payment methods but ${inactiveCount} are inactive. Enable them under Accounts → Payment Methods, then try again.`,
            );
            return;
          }
        } catch { /* fall through to generic message */ }
        toast.error(
          lang === "ar"
            ? "أضف طريقتي دفع نشطتين على الأقل من: الحسابات → طرق الدفع (إنشاء حساب/صندوق وحده لا يكفي)."
            : "Add at least 2 active payment methods under Accounts → Payment Methods (creating a cashbox alone is not enough).",
        );
        return;
      }
      if (accts.length === 0) {
        toast.error(lang === "ar" ? "أضف صندوق/بنك من الحسابات" : "Add a cash/bank account first");
        return;
      }
      setSplitPayOpen(true);
    });
  };

  const waText = (inv) => {
    const lines = [`🧾 ${t("invoice_number")}: ${inv.invoice_number}`, ""];
    inv.items.forEach(i => lines.push(`- ${i.name} x${i.qty}: ${(i.unit_price * i.qty).toFixed(2)}`));
    lines.push("");
    lines.push(`${t("subtotal")}: ${inv.subtotal.toFixed(2)}`);
    if (inv.discount) lines.push(`${t("discount")}: -${inv.discount.toFixed(2)}`);
    if (inv.tax) lines.push(`${t("tax")}: ${inv.tax.toFixed(2)}`);
    lines.push(`${t("total")}: ${inv.total.toFixed(2)}`);
    if (inv.loyalty_earned) lines.push(`⭐ ${t("loyalty_earned")}: ${inv.loyalty_earned}`);
    return lines.join("\n");
  };

  const sendWA = async (inv) => {
    if (!inv.customer_phone) { toast.error(t("customer_phone_not_registered")); return; }
    const tid = toast.loading(lang === "ar" ? "جاري إنشاء PDF وإرسالها..." : "Generating PDF & sending...");
    try {
      const r = await api.post(`/invoices/${inv.invoice_id}/whatsapp`, {});
      toast.dismiss(tid);
      if (r.data?.ok && r.data?.provider_configured) {
        toast.success(lang === "ar" ? "تم إرسال الفاتورة PDF عبر واتساب ✓" : "Invoice PDF sent via WhatsApp ✓");
        return;
      }
      if (r.data?.ok && !r.data?.provider_configured) {
        // Stub mode — wa.me can't attach files, so be honest about what
        // we can offer: open WhatsApp Web with the PDF link, OR ask the
        // cashier to configure Meta WhatsApp Cloud API in Settings to get
        // the PDF attached natively.
        const msg = lang === "ar"
          ? "لإرسال الـPDF كمرفق حقيقي، يحتاج الصالون تفعيل واتساب الأعمال من:\nالإعدادات → واتساب → وضع 'Meta Cloud API'.\n\nهل تريد فتح واتساب الويب الآن مع رابط الـPDF كرسالة نصية (بدون مرفق)؟"
          : "To send the PDF as a true attachment, the salon needs to enable WhatsApp Business under:\nSettings → WhatsApp → Mode 'Meta Cloud API'.\n\nOpen WhatsApp Web now with the PDF link as a text message (no attachment)?";
        setWaStubPrompt({ inv, pdfUrl: r.data.pdf_url, message: msg });
        return;
      }
      toast.error(r.data?.error || (lang === "ar" ? "فشل الإرسال" : "Send failed"));
    } catch (e) {
      toast.dismiss(tid);
      toast.error(e?.response?.data?.detail || (lang === "ar" ? "فشل الإرسال" : "Send failed"));
    }
  };

  const previewPdf = (inv) => {
    // Open the generated A4 PDF in a new tab for download / preview.
    const url = `${API_BASE}/invoices/${inv.invoice_id}/pdf`;
    window.open(url, "_blank");
  };

  const printNow = async (inv, format) => {
    try {
      const r = await api.get("/invoice-template");
      const tpl = r.data;
      const fmt = format || tpl.default_print_format || "pos";
      if (fmt === "pos") printPosReceipt(inv, tpl, lang);
      else printInvoice(inv, tpl, lang);
    } catch {
      // Fallback: print without template
      const fb = {
        language: lang,
        business_name_en: "iSaloon", business_name_ar: "آي سالون",
        show_customer: true, show_phone: true, show_loyalty: true, show_tax: true, show_discount: true,
        primary_color: "#7A1F3D",
        pos: {
          business_name_ar: "آي سالون", business_name_en: "iSaloon",
          header_title_ar: "إيصال", header_title_en: "Receipt",
          footer_text_ar: "شكراً لزيارتكم!", footer_text_en: "Thank you!",
          paper_width_mm: 80, language: lang,
          show_logo: true, show_customer: true, show_phone: true,
          show_tax: true, show_discount: true, show_qr: true, show_loyalty: true,
        },
      };
      const fmt = format || "pos";
      if (fmt === "pos") printPosReceipt(inv, fb, lang);
      else printInvoice(inv, fb, lang);
    }
  };

  const catalog = tab === "services" ? services : tab === "products" ? products : packages;
  const catalogKind = tab === "services" ? "service" : tab === "products" ? "product" : "package";

  // Reset search + category drill-in + paging when the cashier switches
  // between Services/Products/Packages tabs.
  useEffect(() => {
    setCatalogQuery("");
    setCatalogCategory("");
    setVisibleCount(PAGE_SIZE);
  }, [tab]);

  // Reset paging whenever the user types or picks a new category so the
  // first 12 items they see are always the most relevant ones.
  useEffect(() => { setVisibleCount(PAGE_SIZE); }, [catalogQuery, catalogCategory]);

  // Distinct categories present in the current tab — small badge strip
  // sits above the grid, lets the cashier drill in with one tap.
  const catalogCategories = useMemo(() => {
    const seen = new Map();
    for (const it of catalog) {
      const c = (it.category || "").trim();
      if (!c) continue;
      seen.set(c, (seen.get(c) || 0) + 1);
    }
    return Array.from(seen.entries())
      .sort((a, b) => b[1] - a[1])   // most populated first
      .map(([name, count]) => ({ name, count }));
  }, [catalog]);

  // The MATCHED list (full, no paging cap) — used for the "X result(s)"
  // counter and the "Show more" button decision below.
  const matchedCatalog = useMemo(() => {
    const q = catalogQuery.trim().toLowerCase();
    const cat = catalogCategory.trim().toLowerCase();
    return catalog.filter(it => {
      if (cat && (it.category || "").toLowerCase() !== cat) return false;
      if (!q) return true;
      const nameAr = (it.name_ar || "").toLowerCase();
      const nameEn = (it.name_en || "").toLowerCase();
      const sku = (it.sku || "").toLowerCase();
      const itCat = (it.category || "").toLowerCase();
      return nameAr.includes(q) || nameEn.includes(q) || sku.includes(q) || itCat.includes(q);
    });
  }, [catalog, catalogQuery, catalogCategory]);

  // The actually-rendered slice. Search always shows ALL matches (a
  // cashier searching for "keratin" wants to see every match) — only
  // the unfiltered/no-search browse view paginates.
  const filteredCatalog = useMemo(() => {
    if (catalogQuery.trim()) return matchedCatalog;
    return matchedCatalog.slice(0, visibleCount);
  }, [matchedCatalog, catalogQuery, visibleCount]);

  const hasMore = !catalogQuery.trim() && matchedCatalog.length > filteredCatalog.length;

  const normalizeCustomerSearch = (value) => {
    if (!value) return "";
    return (value || "")
      .toLowerCase()
      .normalize("NFKD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim();
  };

  const matchesCustomerSearch = (customer, query) => {
    const q = normalizeCustomerSearch(query);
    if (!q) return false;
    const name = normalizeCustomerSearch(customer.name || "");
    const phoneDigits = (customer.phone || "").replace(/\D/g, "");
    const qDigits = q.replace(/\D/g, "");
    if (name === q || name.startsWith(q)) return true;
    if (qDigits) {
      return phoneDigits === qDigits || phoneDigits.startsWith(qDigits);
    }
    return false;
  };

  const filteredCustomers = useMemo(() => {
    const q = normalizeCustomerSearch(custQuery);
    if (!q) return customers.slice(0, 50);
    return customers.filter(c => matchesCustomerSearch(c, q)).slice(0, 50);
  }, [custQuery, customers]);

  const applyBookingImport = (booking, statusLabel) => {
    const newItems = booking.items.map(it => {
      let name = lang === "ar" ? (it.name_ar || it.name_en) : (it.name_en || it.name_ar);
      let unit = Number(it.price || 0);
      let pkgMeta = null;
      if (it.kind === "service") {
        const found = services.find(s => s.service_id === it.ref_id);
        if (found) { name = lang === "ar" ? found.name_ar : found.name_en; unit = Number(found.price); }
      } else if (it.kind === "package") {
        pkgMeta = packages.find(p => p.package_id === it.ref_id);
        if (pkgMeta) { name = lang === "ar" ? pkgMeta.name_ar : pkgMeta.name_en; unit = Number(pkgMeta.price); }
      }
      return {
        kind: it.kind || "service",
        ref_id: it.ref_id,
        name: name || "",
        unit_price: unit,
        qty: Number(it.qty || 1),
        employee_user_id: it.employee_user_id || (it.kind === "service" ? booking.employee_user_id : null) || null,
        ...(it.selected_services?.length ? {
          selected_services: it.selected_services,
          consumption_reviewed: false,
        } : {}),
        ...(pkgMeta?.package_type ? { package_type: pkgMeta.package_type } : {}),
      };
    }).filter(x => x.ref_id);
    if (newItems.length === 0) {
      toast.error(lang === "ar" ? "تعذر استيراد العناصر" : "Failed to import items");
      return;
    }
    setCart(newItems);
    setLinkedBookingId(booking.booking_id);
    toast.success(lang === "ar"
      ? `تم استيراد ${newItems.length} عنصر من الـ${statusLabel}`
      : `Imported ${newItems.length} items from the ${statusLabel}`);
    window.setTimeout(() => promptConsumptionReviews(newItems), 0);
  };

  const importFromBooking = async (custId, custName) => {
    try {
      const r = await api.get(`/bookings?customer_id=${custId}&status=completed,confirmed&invoiced=false`);
      const bookings = (r.data || []).filter(b => Array.isArray(b.items) && b.items.length > 0);
      if (!bookings.length) return;
      const sorted = [...bookings].sort((a, b) => {
        const cmpDate = (b.date || "").localeCompare(a.date || "");
        if (cmpDate !== 0) return cmpDate;
        const rank = (s) => (s === "completed" ? 0 : s === "confirmed" ? 1 : 2);
        return rank(a.status) - rank(b.status);
      });
      const booking = sorted[0];
      const isConfirmed = booking.status === "confirmed";
      const otherCount = sorted.length - 1;
      const statusLabel = lang === "ar"
        ? (isConfirmed ? "موعد مؤكد" : "موعد مكتمل")
        : (isConfirmed ? "confirmed appointment" : "completed booking");
      const totalPrice = (booking.items || []).reduce((s, it) => s + Number(it.price || 0) * Number(it.qty || 1), 0);
      setBookingImportPrompt({
        booking,
        custName,
        statusLabel,
        otherCount,
        totalPrice,
        timeBit: booking.time_slot ? ` ${booking.time_slot}` : "",
      });
    } catch (err) {
      if (err?.response?.status && err.response.status !== 404) {
        console.error("[POS] importFromBooking failed:", err);
      }
    }
  };

  const selectCustomer = (c) => {
    setCustomerId(c.customer_id);
    setCustQuery(c.name + (c.phone ? ` · ${c.phone}` : ""));
    setCustDropOpen(false);
    setCart((prev) => prev.map((x) => ({
      ...x,
      paid_by_customer_id: c.customer_id || null,
      paid_by_customer_name: c.name || null,
      paid_by_customer_phone: c.phone || "",
    })));
    importFromBooking(c.customer_id, c.name);
  };

  const clearCustomer = () => {
    setCustomerId("");
    setCustQuery("");
    setCustDropOpen(false);
    setCart((prev) => prev.map((x) => ({
      ...x,
      paid_by_customer_id: null,
      paid_by_customer_name: null,
      paid_by_customer_phone: "",
    })));
    setLinkedBookingId(null);
  };

  // NOTE: Tailwind only retains class strings that appear LITERALLY in the
  // source. We therefore inline full class names per tab/palette so the JIT
  // compiler doesn't purge our active-state colors.
  const tabIcons = { services: Scissors, products: PackageIcon, packages: Gift, recent: Receipt };
  const tabPalettes = {
    services: {
      idleBg: "bg-indigo-100", idleText: "text-indigo-700",
      activeBg: "bg-indigo-600", activeShadow: "shadow-indigo-500/30",
      gradFrom: "from-indigo-500", gradTo: "to-violet-600",
    },
    products: {
      idleBg: "bg-emerald-100", idleText: "text-emerald-700",
      activeBg: "bg-emerald-600", activeShadow: "shadow-emerald-500/30",
      gradFrom: "from-emerald-500", gradTo: "to-teal-600",
    },
    packages: {
      idleBg: "bg-rose-100", idleText: "text-rose-700",
      activeBg: "bg-rose-600", activeShadow: "shadow-rose-500/30",
      gradFrom: "from-rose-500", gradTo: "to-pink-600",
    },
    recent: {
      idleBg: "bg-amber-100", idleText: "text-amber-700",
      activeBg: "bg-amber-600", activeShadow: "shadow-amber-500/30",
      gradFrom: "from-amber-500", gradTo: "to-orange-600",
    },
  };

  // POS terminals frequently run at 1024×768 or 1280×800 with touchscreen
  // input. The default Layout reserves 32px padding on `md+` which steals
  // ~64px of horizontal real estate from the catalog. We break out of
  // that padding ONLY on the POS route so the cashier gets the maximum
  // tile area without changing other pages. The negative margins are
  // matched by inner padding so visual rhythm is preserved.
  return (
    <div
      className="pos-route space-y-2 sm:space-y-3 -mx-4 md:-mx-8 px-4 md:px-5 lg:px-6"
      data-testid="pos-page"
    >
      {/* Hero header — slim, single-row layout to maximise catalog/cart visibility */}
      <div className="relative overflow-hidden rounded-xl sm:rounded-2xl bg-gradient-to-br from-white via-white to-rose-50 border border-[var(--border)] shadow-sm px-3 sm:px-4 py-2 sm:py-2.5">
        <div className="absolute -end-12 -top-12 w-32 h-32 rounded-full bg-gradient-to-br from-[var(--gold)] to-rose-500 opacity-[0.08]"/>
        <div className="absolute -start-8 -bottom-8 w-24 h-24 rounded-full bg-gradient-to-br from-indigo-400 to-violet-500 opacity-[0.07]"/>
        <div className="relative flex items-center justify-between gap-2 flex-wrap">
          <div className="flex items-center gap-2 sm:gap-2.5">
            <div className="relative">
              <div className="w-8 h-8 sm:w-9 sm:h-9 rounded-lg sm:rounded-xl bg-gradient-to-br from-[var(--gold)] to-rose-500 text-white flex items-center justify-center shadow-md shadow-rose-500/30">
                <ShoppingCart size={15} className="sm:hidden"/>
                <ShoppingCart size={17} className="hidden sm:block"/>
              </div>
              <div className="absolute -end-1 -top-1 w-2.5 h-2.5 rounded-full bg-amber-400 border-2 border-white animate-pulse"/>
            </div>
            <div className="min-w-0">
              <h1 className="font-heading text-base sm:text-lg gold-text leading-tight">{t("nav_pos")}</h1>
              <p className="hidden md:flex text-[10px] text-[var(--text-muted)] tracking-wider items-center gap-1.5 leading-none mt-0.5">
                <Sparkles size={9} className="text-[var(--gold)]"/>
                {lang === "ar" ? "نقطة بيع سريعة • فواتير وكوبونات وولاء" : "Fast point of sale • Invoices, coupons & loyalty"}
              </p>
              {/* Active-branch badge — clarifies which branch the POS is
                  posting invoices to. Updates live when the branch
                  switcher in the topbar changes the active context. */}
              {posBranchName && (
                <div
                  className="mt-1 inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-wider px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700"
                  data-testid="pos-active-branch"
                  title={lang === "ar"
                    ? "كل الفواتير ستُسجَّل تحت هذا الفرع — يمكنك تبديله من زر الفرع أعلى الصفحة"
                    : "All invoices will be posted to this branch — switch from the branch button at the top"}
                >
                  <Building2 size={9}/>
                  <span>{lang === "ar" ? "فرع:" : "Branch:"}</span>
                  <span className="font-bold">{posBranchName}</span>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center gap-1.5 sm:gap-2">
            <div className="hidden sm:flex px-2.5 py-1 rounded-lg bg-white border border-[var(--border-subtle)] shadow-sm items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-emerald-100 text-emerald-700 flex items-center justify-center">
                <PackageIcon size={12}/>
              </div>
              <div>
                <div className="text-[9px] text-[var(--text-muted)] tracking-wider uppercase leading-none">{lang === "ar" ? "المنتجات" : "Products"}</div>
                <div className="num-display text-xs font-bold text-[var(--text)] leading-none mt-0.5">{products.length}</div>
              </div>
            </div>
            <div className="hidden sm:flex px-2.5 py-1 rounded-lg bg-white border border-[var(--border-subtle)] shadow-sm items-center gap-1.5">
              <div className="w-6 h-6 rounded-md bg-indigo-100 text-indigo-700 flex items-center justify-center">
                <Scissors size={12}/>
              </div>
              <div>
                <div className="text-[9px] text-[var(--text-muted)] tracking-wider uppercase leading-none">{lang === "ar" ? "الخدمات" : "Services"}</div>
                <div className="num-display text-xs font-bold text-[var(--text)] leading-none mt-0.5">{services.length}</div>
              </div>
            </div>
            <div className={`px-2 sm:px-2.5 py-1 rounded-lg border shadow-sm transition flex items-center gap-1.5 ${cart.length > 0 ? "bg-gradient-to-br from-[var(--gold)] to-rose-600 text-white border-transparent shadow-md shadow-rose-500/30" : "bg-white border-[var(--border-subtle)]"}`}>
              <div className={`w-6 h-6 rounded-md flex items-center justify-center ${cart.length > 0 ? "bg-white/20 text-white" : "bg-[var(--gold-tint-strong)] text-[var(--gold)]"}`}>
                <ShoppingCart size={12}/>
              </div>
              <div>
                <div className={`text-[9px] tracking-wider uppercase leading-none ${cart.length > 0 ? "text-white/85" : "text-[var(--text-muted)]"}`}>{lang === "ar" ? "السلة" : "Cart"}</div>
                <div className={`num-display text-xs font-bold leading-none mt-0.5 ${cart.length > 0 ? "text-white" : "text-[var(--text)]"}`}>{cart.length}</div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Side-by-side layout: catalog + compact cart always together (even on phones). */}
      <div className="grid grid-cols-[1fr_180px] sm:grid-cols-[1fr_240px] md:grid-cols-[1fr_290px] lg:grid-cols-[1fr_340px] gap-3 sm:gap-4">
        {/* Catalog */}
        <div className="space-y-3 min-w-0">
          <div className="flex gap-1.5 sm:gap-2 flex-wrap">
            {["services","products","packages","recent"].map(x => {
              const Icon = tabIcons[x];
              const pal = tabPalettes[x];
              const active = tab === x;
              const label = x === "recent"
                ? (lang === "ar" ? "الفواتير" : "Recent")
                : t(`${x}_tab`);
              return (
                <button key={x} onClick={() => { setTab(x); if (x === "recent") loadRecent(""); }} data-testid={`pos-tab-${x}`}
                  className={`inline-flex items-center gap-1.5 px-2.5 sm:px-3.5 py-1.5 sm:py-2 rounded-xl text-xs sm:text-sm font-medium border transition-all duration-200 ${
                    active
                      ? `${pal.activeBg} text-white border-transparent shadow-md ${pal.activeShadow}`
                      : `bg-white ${pal.idleText} border-[var(--border)] hover:shadow-sm hover:-translate-y-0.5`
                  }`}>
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center ${active ? "bg-white/20" : pal.idleBg}`}>
                    <Icon size={11}/>
                  </span>
                  {label}
                </button>
              );
            })}
          </div>

          {tab === "recent" && (
            <div className="space-y-3" data-testid="pos-recent-panel">
              <div className="relative">
                <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)] pointer-events-none" />
                <input
                  type="text"
                  className="input-dark ps-9 pe-9"
                  placeholder={lang === "ar"
                    ? "ابحث برقم الفاتورة، اسم العميل، أو رقم الهاتف..."
                    : "Search by invoice #, customer name, or phone..."}
                  value={recentSearch}
                  onChange={(e) => { setRecentSearch(e.target.value); }}
                  onKeyDown={(e) => { if (e.key === "Enter") loadRecent(recentSearch.trim()); }}
                  data-testid="pos-recent-search"
                />
                {recentSearch && (
                  <button
                    type="button"
                    onClick={() => { setRecentSearch(""); loadRecent(""); }}
                    className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-[var(--text-muted)] hover:text-[var(--text)]"
                    data-testid="pos-recent-clear"
                  >
                    <X size={14} />
                  </button>
                )}
              </div>

              <div className="space-y-2">
                {recentInvoices.length === 0 && (
                  <div className="text-center text-sm text-[var(--text-muted)] py-12 card-surface">
                    {lang === "ar" ? "لا توجد فواتير" : "No invoices found"}
                  </div>
                )}
                {recentInvoices.map((inv) => (
                  <div key={inv.invoice_id} data-testid={`recent-inv-${inv.invoice_id}`} className="group relative overflow-hidden rounded-2xl bg-white border border-[var(--border)] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 p-3.5 flex items-center justify-between gap-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-100 text-amber-700 flex items-center justify-center shrink-0">
                      <Receipt size={16}/>
                    </div>
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="font-mono text-sm font-semibold gold-text">#{inv.invoice_number}</span>
                        {inv.status === "cancelled" && (
                          <span className="text-[10px] px-2 py-0.5 rounded-full bg-rose-100 text-rose-700 font-semibold uppercase tracking-wider" data-testid={`recent-cancelled-${inv.invoice_id}`}>
                            {lang === "ar" ? "ملغاة" : "Cancelled"}
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-[var(--text)] mt-0.5 truncate">{inv.customer_name}{inv.customer_phone ? ` · ${inv.customer_phone}` : ""}</div>
                      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{new Date(inv.created_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")} · {formatPaymentLabel(inv)}</div>
                    </div>
                    <div className="text-end shrink-0">
                      <div className="font-heading text-lg gold-text num-display leading-none">{Number(inv.total).toFixed(2)}</div>
                      <div className="text-[10px] text-[var(--text-muted)] mt-0.5">{lang === "ar" ? "ر.ق" : "QAR"}</div>
                    </div>
                    <div className="flex flex-col gap-1.5 shrink-0">
                      <button onClick={() => { setPreviewInvoice(inv); setPreviewPaymentSuccess(false); }} data-testid={`recent-view-${inv.invoice_id}`} className="text-[10px] px-2.5 py-1 rounded-lg bg-[var(--bg-alt)] hover:bg-[var(--surface-3)] text-[var(--text)] transition font-medium">
                        {lang === "ar" ? "عرض" : "View"}
                      </button>
                      <button onClick={() => printNow(inv, "pos")} data-testid={`recent-print-pos-${inv.invoice_id}`} className="text-[10px] px-2.5 py-1 rounded-lg bg-[var(--gold-tint)] text-[var(--gold)] hover:bg-[var(--gold-tint-strong)] transition font-medium flex items-center gap-1">
                        <Receipt size={10}/>POS
                      </button>
                      <button onClick={() => printNow(inv, "a4")} data-testid={`recent-print-a4-${inv.invoice_id}`} className="text-[10px] px-2.5 py-1 rounded-lg bg-[var(--bg-alt)] hover:bg-[var(--surface-3)] text-[var(--text)] transition font-medium flex items-center gap-1">
                        <FileText size={10}/>A4
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {tab !== "recent" && (
          <>
          {/* Catalog search */}
          <div className="relative">
            <Search size={14} className="absolute top-1/2 -translate-y-1/2 start-3 text-[var(--text-muted)] pointer-events-none" />
            <input
              type="text"
              className="input-dark ps-9 pe-9"
              placeholder={
                lang === "ar"
                  ? `ابحث في ${tab === "services" ? "الخدمات" : tab === "products" ? "المنتجات" : "الباقات"} بالاسم أو الرمز...`
                  : `Search ${tab} by name or SKU...`
              }
              value={catalogQuery}
              onChange={(e) => setCatalogQuery(e.target.value)}
              data-testid="pos-catalog-search"
            />
            {catalogQuery && (
              <button
                type="button"
                onClick={() => setCatalogQuery("")}
                className="absolute top-1/2 -translate-y-1/2 end-2 p-1 text-[var(--text-muted)] hover:text-[var(--text)]"
                data-testid="pos-catalog-search-clear"
              >
                <X size={14} />
              </button>
            )}
            {catalogQuery && (
              <div className="text-xs text-[var(--text-muted)] mt-1.5 px-1">
                {matchedCatalog.length === 0
                  ? (lang === "ar" ? "لا توجد نتائج" : "No matches")
                  : (lang === "ar" ? `${matchedCatalog.length} نتيجة` : `${matchedCatalog.length} result(s)`)}
              </div>
            )}
          </div>

          {/* Category drill-in strip — only when there are categories to show
              AND the user is not actively searching (typed text bypasses
              category filtering so the search result set stays predictable). */}
          {catalogCategories.length > 0 && !catalogQuery && (
            <div className="flex items-center gap-1.5 flex-wrap" data-testid="pos-category-strip">
              <button
                type="button"
                onClick={() => setCatalogCategory("")}
                data-testid="pos-category-all"
                className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${
                  catalogCategory === ""
                    ? "bg-[var(--accent)] text-white border-[var(--accent)] shadow-sm"
                    : "bg-white text-[var(--text)] border-[var(--border)] hover:border-[var(--accent)]"
                }`}
              >
                {lang === "ar" ? "الكل" : "All"}
                <span className="ms-1 opacity-70">({catalog.length})</span>
              </button>
              {catalogCategories.map(({ name, count }) => {
                const active = catalogCategory === name;
                return (
                  <button
                    key={name}
                    type="button"
                    onClick={() => setCatalogCategory(active ? "" : name)}
                    data-testid={`pos-category-${name}`}
                    className={`px-3 py-1.5 rounded-full text-[11px] font-semibold border transition-all ${
                      active
                        ? "bg-[var(--accent)] text-white border-[var(--accent)] shadow-sm"
                        : "bg-white text-[var(--text)] border-[var(--border)] hover:border-[var(--accent)]"
                    }`}
                  >
                    {name}
                    <span className="ms-1 opacity-70">({count})</span>
                  </button>
                );
              })}
            </div>
          )}

          <div
            className="grid gap-2 sm:gap-2.5"
            style={{ gridTemplateColumns: "repeat(auto-fill, minmax(124px, 1fr))" }}
          >
            {filteredCatalog.length === 0 && !catalogQuery && (
              <div className="col-span-full text-center py-10 px-6 rounded-2xl border border-dashed border-[var(--border)] bg-white">
                <PackageIcon size={28} className="mx-auto text-[var(--text-disabled)] mb-2"/>
                <div className="text-xs text-[var(--text-muted)]">{lang === "ar" ? "لا توجد عناصر" : "No items"}</div>
              </div>
            )}
            {filteredCatalog.map(it => {
              const id = it.service_id || it.product_id || it.package_id;
              const onSale = tab === "products" && it.sale_price != null;
              const pal = tabPalettes[tab];
              const Icon = tabIcons[tab];
              const img = resolveImg(it.image_url);
              const itemName = lang === "ar" ? it.name_ar : it.name_en;
              const displayPrice = onSale ? it.sale_price : it.price;
              const priceLabel = Number(displayPrice ?? 0).toFixed(2);
              const showDuration = it.duration_min && (tab === "services" || tab === "packages");
              return (
                <button key={id} onClick={() => handleCatalogAdd(catalogKind, it)}
                  data-testid={`catalog-item-${id}`}
                  className="group relative aspect-square overflow-hidden rounded-xl bg-white border border-[var(--border)] shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-300 text-start">
                  {/* Full-bleed image or themed gradient */}
                  {img ? (
                    <img
                      src={img}
                      alt={itemName}
                      loading="lazy"
                      className="absolute inset-0 w-full h-full object-cover group-hover:scale-110 transition-transform duration-500"
                      onError={(e) => { e.currentTarget.style.display = "none"; }}
                    />
                  ) : (
                    <div className={`absolute inset-0 bg-gradient-to-br ${pal.gradFrom} ${pal.gradTo}`}>
                      <Icon className="absolute top-1/3 left-1/2 -translate-x-1/2 -translate-y-1/2 text-white/35" size={28}/>
                      <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.16),transparent_60%)]"/>
                    </div>
                  )}

                  {/* Dark gradient overlay for legibility */}
                  <div className="absolute inset-x-0 bottom-0 h-3/5 bg-gradient-to-t from-black/85 via-black/50 to-transparent"/>

                  {/* Top corners: sale + stock badges */}
                  {tab === "packages" && it.package_type === "pick_n" && (
                    <span className="absolute top-1 end-1 text-[8px] font-bold px-1.5 py-0.5 rounded bg-[var(--gold)] text-black shadow-sm">
                      {lang === "ar" ? `اختر ${it.pick_count || 0}` : `Pick ${it.pick_count || 0}`}
                    </span>
                  )}
                  {onSale && (
                    <span className="absolute top-1 start-1 text-[8px] font-bold uppercase tracking-wider px-1 py-0.5 rounded bg-rose-500 text-white shadow-sm">
                      {lang === "ar" ? "عرض" : "Sale"}
                    </span>
                  )}
                  {tab === "products" && (
                    <span className={`absolute top-1 end-1 text-[8px] font-bold px-1 py-0.5 rounded shadow-sm num-display ${it.stock <= it.low_stock_threshold ? "bg-rose-600 text-white" : "bg-emerald-500 text-white"}`}>
                      {it.stock}
                    </span>
                  )}

                  {/* Quick add chip (hover, desktop only) */}
                  <div className="hidden md:flex absolute top-1.5 start-1/2 -translate-x-1/2 w-6 h-6 rounded-full bg-white text-[var(--gold)] shadow-md items-center justify-center opacity-0 group-hover:opacity-100 group-hover:scale-100 scale-75 transition-all duration-300 pointer-events-none">
                    <Plus size={12}/>
                  </div>

                  {/* Bottom content: name + price */}
                  <div className="absolute inset-x-0 bottom-0 p-1.5 pos-catalog-overlay-text">
                    <div className="text-[10px] font-semibold line-clamp-1 leading-tight mb-0.5 drop-shadow" title={itemName}>{itemName}</div>
                    <div className="flex items-end justify-between gap-1">
                      <div className="flex items-baseline gap-0.5 min-w-0">
                        <span className="text-xs font-semibold num-display drop-shadow">{priceLabel}</span>
                        {onSale && (
                          <span className="text-[8px] line-through opacity-70 num-display">{Number(it.price ?? 0).toFixed(2)}</span>
                        )}
                        <span className="text-[7px] opacity-85 tracking-wider">{lang === "ar" ? "ر.ق" : "QAR"}</span>
                      </div>
                      {showDuration && (
                        <span className="text-[8px] opacity-85 flex items-center gap-0.5 num-display shrink-0">
                          <Clock size={7}/>{it.duration_min}m
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              );
            })}
          </div>

          {/* "Show more" footer — only when paginated and more items remain. */}
          {hasMore && (
            <div className="flex items-center justify-center pt-2">
              <button
                type="button"
                onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                data-testid="pos-show-more"
                className="px-4 py-2 rounded-full text-xs font-semibold border border-[var(--border)] bg-white hover:bg-[var(--accent-soft)] hover:border-[var(--accent)] hover:text-[var(--accent)] transition-all shadow-sm"
              >
                {lang === "ar"
                  ? `عرض المزيد (${matchedCatalog.length - filteredCatalog.length})`
                  : `Show more (${matchedCatalog.length - filteredCatalog.length})`}
              </button>
            </div>
          )}
          </>
          )}
        </div>

        {/* Cart — narrow & sticky for mobile side-by-side */}
        <div className="rounded-2xl bg-white border border-[var(--border)] shadow-sm p-2.5 sm:p-4 flex flex-col h-fit sticky top-3 min-w-0">
          <div className="flex items-center justify-between mb-2.5 sm:mb-3.5">
            <div className="flex items-center gap-2 min-w-0">
              <div className="w-7 h-7 sm:w-8 sm:h-8 rounded-lg bg-gradient-to-br from-[var(--gold)] to-rose-500 text-white flex items-center justify-center shadow-sm shrink-0">
                <ShoppingCart size={13}/>
              </div>
              <div className="min-w-0">
                <div className="font-heading text-sm text-[var(--text)] leading-none">{t("cart")}</div>
                {cart.length > 0 && <div className="text-[9px] text-[var(--text-muted)] tracking-wider mt-0.5">{cart.length} {lang === "ar" ? "عنصر" : "items"}</div>}
              </div>
            </div>
            {cart.length > 0 && <button onClick={resetCheckoutState} className="text-[10px] text-[var(--text-muted)] hover:text-rose-600 transition shrink-0">{t("clear_cart")}</button>}
          </div>

          <div className="mb-2.5 relative" ref={custBoxRef}>
            <label className="text-[10px] text-[var(--text-muted)] tracking-wider uppercase block mb-1">{t("customer")}</label>
            <div className="relative">
              <Search size={12} className="absolute top-1/2 -translate-y-1/2 start-2.5 text-[var(--text-muted)] pointer-events-none" />
              <input
                type="text"
                className="input-dark ps-8 pe-7 text-xs py-1.5"
                placeholder={lang === "ar" ? "ابحث بالاسم..." : "Search..."}
                value={custQuery}
                onChange={(e) => { setCustQuery(e.target.value); setCustDropOpen(true); if (!e.target.value) setCustomerId(""); }}
                onFocus={() => setCustDropOpen(true)}
                data-testid="pos-customer-search"
              />
              {(customerId || custQuery) && (
                <button type="button" onClick={clearCustomer} className="absolute top-1/2 -translate-y-1/2 end-1.5 p-0.5 text-[var(--text-muted)] hover:text-red-400" data-testid="pos-customer-clear">
                  <X size={12} />
                </button>
              )}
            </div>
            {custDropOpen && (
              <div className="absolute z-30 mt-1 left-0 right-0 max-h-72 overflow-auto rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-xl" data-testid="pos-customer-dropdown">
                <button
                  type="button"
                  onClick={() => { setCustomerId(""); setCustQuery(""); setCustDropOpen(false); }}
                  className="w-full text-start px-3 py-2 text-sm text-[var(--text-muted)] hover:bg-[var(--surface-2)] border-b border-[var(--border-subtle)]"
                  data-testid="pos-customer-walkin"
                >
                  {t("walk_in")}
                </button>
                {filteredCustomers.length === 0 ? (
                  <div className="px-3 py-3 text-sm text-[var(--text-muted)]">{lang === "ar" ? "لا توجد نتائج" : "No matches"}</div>
                ) : filteredCustomers.map(c => (
                  <button
                    key={c.customer_id}
                    type="button"
                    onClick={() => selectCustomer(c)}
                    className={`w-full text-start px-3 py-2 text-sm hover:bg-[var(--surface-2)] flex items-center justify-between gap-3 ${customerId === c.customer_id ? "bg-[var(--surface-2)] text-[var(--gold)]" : "text-[var(--text)]"}`}
                    data-testid={`pos-customer-opt-${c.customer_id}`}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{c.name}</div>
                      {c.phone && <div className="text-xs text-[var(--text-muted)]" dir="ltr">{c.phone}</div>}
                    </div>
                    {c.membership_tier && <span className={`badge tier-${c.membership_tier} text-[9px]`}>{c.membership_tier}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>

          <div className="space-y-1.5 max-h-64 sm:max-h-80 overflow-auto pe-0.5">
            {cart.map((it, i) => {
              const thumb = resolveImg(it.image_url);
              const KindIcon = it.kind === "product" ? PackageIcon : it.kind === "package" ? Gift : Scissors;
              return (
                <div key={`${it.kind}:${it.ref_id}:${i}`} className="flex items-center gap-1.5 bg-[var(--bg-alt)] border border-[var(--border-subtle)] rounded-lg p-1.5 hover:border-[var(--gold)]/40 hover:shadow-sm transition">
                  <div className="w-8 h-8 rounded-md overflow-hidden shrink-0 bg-gradient-to-br from-[var(--gold)] to-rose-500 flex items-center justify-center text-white shadow-sm">
                    {thumb ? (
                      <img src={thumb} alt={it.name} className="w-full h-full object-cover" loading="lazy" onError={(e)=>{ e.currentTarget.style.display="none"; }}/>
                    ) : (
                      <KindIcon size={13}/>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] text-[var(--text)] truncate font-medium leading-tight" title={it.name}>{it.name}</div>
                    {(it.selected_services || []).length > 0 && (
                      <div className="mt-0.5 space-y-0.5">
                        {it.selected_services.map((sel) => (
                          <div key={sel.service_id} className="text-[9px] text-[var(--text-muted)] truncate ps-1 border-s border-[var(--gold)]/30 flex items-center gap-1">
                            <span>• {selectedServiceLabel(sel, lang)}</span>
                            {sel.status === "pending" && (
                              <span className="text-[8px] px-1 rounded bg-amber-100 text-amber-800 shrink-0">
                                {lang === "ar" ? "متابعة" : "Follow-up"}
                              </span>
                            )}
                          </div>
                        ))}
                      </div>
                    )}
                    {lineNeedsConsumptionReview(it) && (
                      <button
                        type="button"
                        onClick={() => openConsumptionReviewAtIndex(cart, i)}
                        className="mt-1 text-[9px] px-1.5 py-0.5 rounded border border-amber-400 text-amber-700"
                        data-testid={`cart-review-package-${i}`}
                      >
                        {lang === "ar" ? "تحديد المنجز / المعلق" : "Set done / follow-up"}
                      </button>
                    )}
                    <div className="text-[9px] text-[var(--text-muted)] num-display tracking-wider mt-0.5">
                      {it.discount_type && it.discount_type !== "none" && it.discount_value > 0 ? (
                        <>
                          <span className="line-through text-stone-400 me-1">{(it.unit_price*it.qty).toFixed(2)}</span>
                          <span className="text-emerald-600 font-semibold">{(() => {
                            const ls = it.unit_price * it.qty;
                            const d = it.discount_type === "percent" ? Math.min(it.discount_value,100)/100*ls : Math.min(it.discount_value, ls);
                            return (ls - d).toFixed(2);
                          })()}</span>
                          <span className="text-[8px] text-rose-500 ms-1">{it.discount_type === "percent" ? `(-${it.discount_value}%)` : `(-${it.discount_value})`}</span>
                        </>
                      ) : (
                        <span className="text-[var(--gold)] font-semibold">{(it.unit_price*it.qty).toFixed(2)}</span>
                      )}
                    </div>
                    {/* Staff selector — required for commission attribution on this line. */}
                    {!(it.kind === "package" && (it.selected_services || []).length > 0) && (
                    <select
                      value={it.employee_user_id || ""}
                      onChange={(e) => setLineEmployee(i, e.target.value)}
                      data-testid={`cart-staff-${i}`}
                      onMouseDown={onModalPanelMouseDown}
                      className={`mt-1 w-full text-[10px] px-1.5 py-0.5 rounded border bg-white truncate ${it.employee_user_id ? "border-[var(--gold)]/40 text-[var(--text)]" : "border-rose-200 text-rose-500"}`}
                      title={lang === "ar" ? "الموظف الذي أدى الخدمة (للعمولة)" : "Staff who performed this (for commission)"}
                    >
                      <option value="">{lang === "ar" ? "— موظف —" : "— Staff —"}</option>
                      {staff.map((u) => (
                        <option key={u.user_id} value={u.user_id}>{u.name || u.username}</option>
                      ))}
                    </select>
                    )}
                    <div className="mt-1 grid gap-1">
                      <div className="text-[9px] uppercase tracking-wider text-[var(--text-muted)]">
                        {lang === "ar" ? "المستفيد من الخدمة" : "Service recipient"}
                      </div>
                      <div className="relative">
                        <input
                          type="text"
                          value={lineRecipientQuery[i] ?? it.recipient_customer_name ?? ""}
                          onFocus={() => setLineRecipientOpen((o) => ({ ...o, [i]: true }))}
                          onChange={(e) => {
                            const raw = e.target.value;
                            setLineRecipientQuery((q) => ({ ...q, [i]: raw }));
                            setLineRecipientOpen((o) => ({ ...o, [i]: true }));
                            if (!raw.trim()) {
                              setLineRecipient(i, null);
                              return;
                            }
                            const match = findCustomerByQuery(raw);
                            if (match) {
                              setLineRecipient(i, match);
                              setLineRecipientQuery((q) => ({ ...q, [i]: match.name || raw }));
                            } else {
                              setLineRecipient(i, null);
                            }
                          }}
                          placeholder={lang === "ar" ? "ابحث بالاسم/الهاتف" : "Search by name/phone"}
                          className="w-full text-[10px] px-1.5 py-0.5 rounded border border-[var(--border)] bg-white"
                        />
                        {lineRecipientOpen[i] && (() => {
                          const matches = (lineRecipientQuery[i] || "").trim()
                            ? customers.filter((c) => matchesCustomerSearch(c, lineRecipientQuery[i])).slice(0, 6)
                            : [];
                          return matches.length > 0 ? (
                            <div className="absolute z-20 mt-1 left-0 right-0 max-h-40 overflow-auto rounded-md border border-[var(--border)] bg-[var(--surface)] shadow-xl">
                              {matches.map((c) => (
                                <button
                                  key={c.customer_id}
                                  type="button"
                                  className="w-full text-left px-2 py-1 text-[10px] hover:bg-[var(--surface-2)]"
                                  onClick={() => {
                                    setLineRecipient(i, c);
                                    setLineRecipientQuery((q) => ({ ...q, [i]: c.name || "" }));
                                    setLineRecipientOpen((o) => ({ ...o, [i]: false }));
                                  }}
                                >
                                  <div className="font-medium">{c.name}</div>
                                  {c.phone && <div className="text-[9px] text-[var(--text-muted)]">{c.phone}</div>}
                                </button>
                              ))}
                            </div>
                          ) : null;
                        })()}
                      </div>
                    </div>
                  </div>
                  {/* Line-discount button — opens the LineDiscountEditor modal */}
                  {discountPolicy?.enabled !== false && (
                    <button
                      onClick={() => setEditingDiscountIdx(i)}
                      data-testid={`cart-line-discount-${i}`}
                      title={lang === "ar" ? "خصم على هذا البند" : "Line discount"}
                      className={`w-7 h-7 sm:w-8 sm:h-8 rounded flex items-center justify-center shrink-0 transition ${
                        it.discount_type && it.discount_type !== "none" && it.discount_value > 0
                          ? "bg-amber-50 text-amber-700 border border-amber-300"
                          : "hover:bg-stone-100 text-stone-400 border border-transparent"
                      }`}
                    >
                      <Tag size={12}/>
                    </button>
                  )}
                  <div className="flex items-center gap-0.5 bg-white rounded-md border border-[var(--border-subtle)] p-0.5 shrink-0">
                    {/* Touch-friendly +/-/x targets: 28px on phones, 32px
                        on POS terminals (sm:) — still compact for a busy
                        cart panel but comfortable to thumb-tap. */}
                    <button
                      onClick={() => updateQty(i, -1)}
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded hover:bg-[var(--bg-alt)] active:bg-[var(--bg-alt)] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text)] transition"
                      data-testid="cart-qty-down"
                    >
                      <Minus size={12}/>
                    </button>
                    <span className="text-xs sm:text-sm w-5 text-center num-display font-semibold">{it.qty}</span>
                    <button
                      onClick={() => updateQty(i, 1)}
                      className="w-7 h-7 sm:w-8 sm:h-8 rounded hover:bg-[var(--bg-alt)] active:bg-[var(--bg-alt)] flex items-center justify-center text-[var(--text-muted)] hover:text-[var(--text)] transition"
                      data-testid="cart-qty-up"
                    >
                      <Plus size={12}/>
                    </button>
                  </div>
                  <button
                    onClick={() => removeItem(i)}
                    className="w-7 h-7 sm:w-8 sm:h-8 rounded hover:bg-rose-50 active:bg-rose-100 hover:text-rose-600 flex items-center justify-center text-[var(--text-muted)] transition shrink-0"
                    data-testid="cart-remove"
                  >
                    <X size={12}/>
                  </button>
                </div>
              );
            })}
            {cart.length === 0 && (
              <div className="text-center py-6 px-2 rounded-xl border border-dashed border-[var(--border)] bg-[var(--bg-alt)]/60">
                <ShoppingCart size={22} className="mx-auto text-[var(--text-disabled)] mb-1.5"/>
                <div className="text-[10px] text-[var(--text-muted)] leading-snug">{lang === "ar" ? "السلة فارغة" : "Cart is empty"}</div>
              </div>
            )}
          </div>

          <div className="mt-3 sm:mt-4 space-y-2">
            <div>
              <label className="text-[10px] text-[var(--text-muted)] tracking-wider uppercase block mb-1">{t("discount")}</label>
              <input type="number" className="input-dark text-sm py-1.5" value={discount} onChange={e => setDiscount(e.target.value)}/>
            </div>
            <div>
              <label className="text-[10px] text-[var(--text-muted)] tracking-wider uppercase block mb-1">{t("tax_rate")}</label>
              <input type="number" className="input-dark text-sm py-1.5" value={taxRate} onChange={e => setTaxRate(e.target.value)}/>
            </div>
            <div>
              <label className="text-[10px] text-[var(--text-muted)] tracking-wider uppercase block mb-1">{t("payment_method")}</label>
              <select className="input-dark text-sm py-1.5" value={method} onChange={e => onMethodChange(e.target.value)} data-testid="pos-payment-method">
                {paymentMethods.length === 0 && <option value="">—</option>}
                {paymentMethods.map(m => {
                  const typeLabel = lang === "ar"
                    ? { cash: "نقدي", card: "بطاقة", bank: "بنك", wallet: "محفظة", other: "أخرى" }[m.type] || m.type
                    : m.type;
                  return (
                    <option key={m.method_id} value={m.method_id}>
                      {m.name} {m.name.toLowerCase() !== typeLabel.toLowerCase() ? `(${typeLabel})` : ""}
                    </option>
                  );
                })}
              </select>
            </div>
            {accounts.length > 0 && (
              <div>
                <label className="text-[10px] text-[var(--text-muted)] tracking-wider uppercase block mb-1">{t("nav_accounts")} <span className="text-rose-500">*</span></label>
                <select
                  className={`input-dark text-sm py-1.5 ${!accountId ? "border-rose-400/60" : ""}`}
                  value={accountId}
                  onChange={e => setAccountId(e.target.value)}
                  data-testid="pos-account"
                  required
                >
                  <option value="">{lang === "ar" ? "— اختر —" : "— Select —"}</option>
                  {accounts.map(a => <option key={a.account_id} value={a.account_id}>{a.name}</option>)}
                </select>
              </div>
            )}
            {selectedCust && (
              <div className="bg-gradient-to-br from-[var(--gold-tint-strong)] to-[var(--gold-tint)] border border-[var(--gold)]/40 rounded-md p-3 space-y-2.5" data-testid="pos-loyalty-panel">
                <div className="flex items-center justify-between flex-wrap gap-2">
                  <div className="flex items-center gap-2">
                    <Sparkles size={15} className="text-[var(--gold)]"/>
                    <span className="text-xs font-semibold uppercase tracking-wider text-[var(--gold)]">
                      {lang === "ar" ? "الولاء" : "Loyalty"}
                    </span>
                    {selectedCust.membership_tier && (
                      <span className={`badge tier-${selectedCust.membership_tier} text-[9px]`}>{selectedCust.membership_tier}</span>
                    )}
                  </div>
                  <div className="text-end">
                    <div className="font-heading text-2xl gold-text num-display leading-none" data-testid="pos-loyalty-balance">{maxRedeem}</div>
                    <div className="text-[10px] text-[var(--text-muted)] mt-0.5">
                      {lang === "ar" ? `≈ ${(maxRedeem / 30).toFixed(2)} ر.ق` : `≈ ${(maxRedeem / 30).toFixed(2)} QAR`}
                    </div>
                  </div>
                </div>
                {maxRedeem > 0 ? (
                  <>
                    <div className="flex items-center gap-2">
                      <input
                        type="number"
                        min="0"
                        max={maxRedeem}
                        className="input-dark flex-1 num-display text-sm"
                        value={redeemPoints}
                        onChange={e => {
                          const v = Math.min(maxRedeem, Math.max(0, parseInt(e.target.value) || 0));
                          setRedeemPoints(v);
                        }}
                        data-testid="pos-redeem"
                        placeholder={lang === "ar" ? "استبدال نقاط..." : "Redeem points..."}
                      />
                      <button
                        type="button"
                        onClick={() => setRedeemPoints(maxRedeem)}
                        className="btn-secondary px-3 py-2 rounded text-xs whitespace-nowrap"
                        data-testid="pos-redeem-max"
                      >
                        {lang === "ar" ? "استخدم الكل" : "Use all"}
                      </button>
                      {redeemPoints > 0 && (
                        <button
                          type="button"
                          onClick={() => setRedeemPoints(0)}
                          className="btn-ghost px-2 py-2 rounded text-xs"
                          data-testid="pos-redeem-clear"
                        >
                          <X size={12}/>
                        </button>
                      )}
                    </div>
                    {redeemPoints > 0 && (
                      <div className="text-[11px] text-[var(--text-muted)]">
                        {lang === "ar"
                          ? `استبدال ${redeemPoints} نقطة = خصم ${(redeemPoints / 30).toFixed(2)} ر.ق`
                          : `Redeeming ${redeemPoints} points = ${(redeemPoints / 30).toFixed(2)} QAR off`}
                      </div>
                    )}
                    <div className="text-[10px] text-[var(--text-disabled)]">
                      {lang === "ar" ? "30 نقطة = 1 ر.ق" : "30 points = 1 QAR"}
                    </div>
                  </>
                ) : (
                  <div className="text-xs text-[var(--text-muted)]">
                    {lang === "ar" ? "لا توجد نقاط متاحة لهذا العميل" : "No points available for this customer"}
                  </div>
                )}
                <div className="flex items-center justify-between pt-2 border-t border-[var(--gold)]/20 text-[10px] text-[var(--text-muted)]">
                  <span>{lang === "ar" ? "إجمالي المشتريات" : "Total spent"}:</span>
                  <span className="num-display gold-text">
                    {(selectedCust.total_spent || 0).toFixed(2)} {lang === "ar" ? "ر.ق" : "QAR"}
                  </span>
                </div>
              </div>
            )}
          </div>

          <div className="gold-divider my-3"/>

          {/* Coupon code section */}
          <div className="space-y-2 mb-3" data-testid="pos-coupon-section">
            <label className="text-xs text-[var(--text-muted)] flex items-center gap-1.5"><Tag size={12} className="text-[var(--gold)]"/>{lang === "ar" ? "كود الكوبون" : "Coupon Code"}</label>
            {couponInfo ? (
              <div className="flex items-center justify-between gap-2 px-3 py-2 rounded-md border border-[var(--gold)] bg-[var(--gold-tint)]" data-testid="pos-coupon-applied">
                <div className="flex items-center gap-2 min-w-0">
                  <Check size={14} className="text-[var(--gold)] shrink-0"/>
                  <div className="min-w-0">
                    <div className="text-xs font-semibold gold-text truncate">{couponInfo.code || couponCode}</div>
                    <div className="text-[10px] text-[var(--text-muted)]">
                      {lang === "ar" ? "خصم" : "Discount"}: {couponDiscount.toFixed(2)} {lang === "ar" ? "ر.ق" : "QAR"}
                    </div>
                  </div>
                </div>
                <button type="button" onClick={clearCoupon} data-testid="pos-coupon-remove" className="text-[var(--text-muted)] hover:text-[var(--rose)] p-1 transition"><X size={14}/></button>
              </div>
            ) : (
              <>
                <div className="flex gap-2">
                  <input
                    className="input-dark flex-1 uppercase text-sm"
                    placeholder={lang === "ar" ? "أدخل الكود" : "Enter code"}
                    value={couponCode}
                    onChange={e => { setCouponCode(e.target.value.toUpperCase()); setCouponError(""); }}
                    onKeyDown={e => { if (e.key === "Enter") { e.preventDefault(); applyCoupon(); } }}
                    data-testid="pos-coupon-input"
                  />
                  <button
                    type="button"
                    onClick={applyCoupon}
                    disabled={couponChecking || !couponCode.trim()}
                    className="btn-secondary px-3 py-2 rounded-md text-xs disabled:opacity-50 disabled:cursor-not-allowed"
                    data-testid="pos-coupon-apply"
                  >
                    {couponChecking ? (lang === "ar" ? "..." : "...") : (lang === "ar" ? "تطبيق" : "Apply")}
                  </button>
                </div>
                {couponError && (
                  <div className="text-[11px] text-[var(--rose)] flex items-center gap-1" data-testid="pos-coupon-error"><AlertCircle size={11}/>{couponError}</div>
                )}
              </>
            )}
          </div>

          <div className="space-y-1 text-xs rounded-lg bg-[var(--bg-alt)] border border-[var(--border-subtle)] p-2 sm:p-2.5">
            <div className="flex justify-between"><span className="text-[var(--text-muted)] text-[10px]">{t("subtotal")}</span><span className="num-display text-[var(--text)]">{subtotal.toFixed(2)}</span></div>
            {lineDiscountTotal > 0 && (
              <div className="flex justify-between text-rose-500" data-testid="cart-line-discount-total">
                <span className="text-[10px]">{lang === "ar" ? "خصومات البنود" : "Line discounts"}</span>
                <span className="num-display">-{lineDiscountTotal.toFixed(2)}</span>
              </div>
            )}
            <div className="flex justify-between"><span className="text-[var(--text-muted)] text-[10px]">{t("discount")}</span><span className="num-display text-[var(--text)]">-{(+discount || 0).toFixed(2)}</span></div>
            {couponDiscount > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)] text-[10px] flex items-center gap-1"><Tag size={9}/>{lang === "ar" ? "كوبون" : "Coupon"}</span><span className="gold-text num-display font-semibold">-{couponDiscount.toFixed(2)}</span></div>}
            {redeemValue > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)] text-[10px] flex items-center gap-1"><Star size={9}/>{t("points_value")}</span><span className="rose-text num-display font-semibold">-{redeemValue.toFixed(2)}</span></div>}
            <div className="flex justify-between"><span className="text-[var(--text-muted)] text-[10px]">{t("tax")}</span><span className="num-display text-[var(--text)]">{tax.toFixed(2)}</span></div>
            {staffPaySettings.tips_enabled !== false && (
              <div className="space-y-1.5 pt-1 border-t border-[var(--border-subtle)]" data-testid="pos-tip-section">
                <div className="flex justify-between items-center gap-2">
                  <span className="text-[var(--text-muted)] text-[10px]">{t("tip")}</span>
                  <input
                    type="number"
                    min="0"
                    step="0.01"
                    className="input-dark text-xs py-1 w-24 text-end num-display"
                    value={tipAmount || ""}
                    onChange={(e) => {
                      const v = parseFloat(e.target.value) || 0;
                      setTipAmount(v);
                      setTipSplits(v > 0 ? computeProportionalTipSplits(cart, v) : []);
                    }}
                    data-testid="pos-tip-amount"
                  />
                </div>
                <div className="flex flex-wrap gap-1">
                  {(staffPaySettings.default_tip_presets || [10, 15, 20]).map((p) => (
                    <button
                      key={p}
                      type="button"
                      onClick={() => applyTipPercent(p)}
                      className="px-2 py-0.5 rounded text-[10px] border border-[var(--border)] hover:border-[var(--gold)]"
                      data-testid={`pos-tip-preset-${p}`}
                    >
                      {p}%
                    </button>
                  ))}
                  {tipTotal > 0 && (
                    <button
                      type="button"
                      onClick={() => setTipSplitOpen(true)}
                      className="btn-soft px-2 py-0.5 rounded text-[10px] font-medium"
                      data-testid="pos-tip-split-btn"
                    >
                      {t("tip_split")}
                    </button>
                  )}
                </div>
              </div>
            )}
            <div className="mt-1.5 pt-2 border-t border-[var(--border-subtle)] flex items-center justify-between bg-gradient-to-r from-[var(--gold-tint)] to-transparent rounded-md -mx-1 px-2 py-1.5">
              <span className="font-heading text-xs sm:text-sm text-[var(--text)]">{tipTotal > 0 ? t("amount_due") : t("total")}</span>
              <div className="text-end">
                <div className="font-heading text-lg sm:text-xl gold-text num-display leading-none">{amountDue.toFixed(2)}</div>
                {tipTotal > 0 && (
                  <div className="text-[9px] text-[var(--text-muted)] num-display">{total.toFixed(2)} + {tipTotal.toFixed(2)} {t("tip")}</div>
                )}
                <div className="text-[9px] text-[var(--text-muted)] tracking-wider mt-0.5">{lang === "ar" ? "ر.ق" : "QAR"}</div>
              </div>
            </div>
          </div>

          <div className="flex flex-col gap-2 mt-2.5 sm:mt-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <button
                onClick={checkout}
                disabled={checkoutBusy}
                data-testid="pos-checkout-btn"
                className="flex-1 py-2.5 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm tracking-wider uppercase bg-gradient-to-r from-[var(--gold)] to-rose-600 text-white shadow-md hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <Wallet size={14}/>
                <span>{t("pay")}</span>
                <span className="num-display">• {amountDue.toFixed(2)}</span>
              </button>
              <button
                type="button"
                onClick={openSplitPay}
                disabled={checkoutBusy}
                data-testid="pos-split-pay-btn"
                className="flex-1 py-2.5 sm:py-3 rounded-xl font-semibold text-xs sm:text-sm tracking-wider uppercase btn-secondary hover:border-[var(--gold)] hover:text-[var(--gold)] transition-all duration-200 flex items-center justify-center gap-1.5 disabled:opacity-50"
              >
                <CreditCard size={14}/>
                <span>{t("split_pay")}</span>
              </button>
            </div>
            {/*
              Manual cash-drawer trigger. Lives next to the checkout button
              because the cashier reaches for them in the same motion. First
              click on a fresh browser session shows the WebSerial picker so
              the operator can pair the printer/drawer once; subsequent
              clicks open instantly (port reused).
            */}
            <button
              type="button"
              onClick={async () => {
                // Force a prompt on the very first click so the cashier
                // can pair the device explicitly. After pairing, the cached
                // port handles every subsequent open silently.
                const supportsSerial = "serial" in navigator;
                const ports = supportsSerial ? await navigator.serial.getPorts().catch(() => []) : [];
                const allowPrompt = supportsSerial && ports.length === 0;
                if (allowPrompt) await pairCashDrawer({ lang });
                openCashDrawer({ source: "manual-button", allowPrompt: false, lang });
              }}
              className="sm:w-auto px-4 py-2.5 sm:py-3 rounded-xl border border-[var(--gold)] text-[var(--gold)] hover:bg-[var(--gold-tint)] active:bg-[var(--gold)]/15 transition flex items-center justify-center gap-2 text-xs sm:text-sm font-semibold"
              data-testid="pos-open-drawer-btn"
              title={lang === "ar" ? "فتح الصندوق يدوياً" : "Open cash drawer"}
            >
              <Inbox size={16}/>
              <span className="sm:hidden md:inline">{lang === "ar" ? "فتح الصندوق" : "Open Drawer"}</span>
            </button>
          </div>
        </div>
      </div>

      {/* Invoice details modal (post-checkout + recent tab) */}
      {previewInvoice && (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => { setPreviewInvoice(null); setPreviewPaymentSuccess(false); })}>
          <div className="card-surface max-w-lg w-full p-6 max-h-[90vh] overflow-auto" onMouseDown={onModalPanelMouseDown} data-testid="invoice-preview-modal">
            {previewPaymentSuccess && (
              <div className="mb-4 flex items-center gap-3 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-4 py-3" data-testid="payment-success-banner">
                <Check size={20} className="text-emerald-400 shrink-0" />
                <div>
                  <div className="font-medium text-emerald-300">{t("payment_success")}</div>
                  <div className="text-xs text-[var(--text-muted)] mt-0.5">
                    {lang === "ar" ? "يمكنك طباعة الإيصال أو إرسال الفاتورة أدناه." : "You can print the receipt or send the invoice below."}
                  </div>
                </div>
              </div>
            )}
            <div className="flex items-center justify-between mb-4">
              <div>
                <div className="font-heading text-xl gold-text">#{previewInvoice.invoice_number}</div>
                <div className="text-xs text-[var(--text-muted)] mt-1">{new Date(previewInvoice.created_at).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")}</div>
              </div>
              <div className="flex items-center gap-2">
                {previewInvoice.status === "cancelled" && <span className="badge badge-red">{lang === "ar" ? "ملغاة" : "CANCELLED"}</span>}
                <button onClick={() => { setPreviewInvoice(null); setPreviewPaymentSuccess(false); }} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18}/></button>
              </div>
            </div>
            <div className="space-y-1 text-sm mb-4">
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("customer")}</span><span className="font-medium">{previewInvoice.customer_name}</span></div>
              {previewInvoice.customer_phone && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{lang === "ar" ? "الهاتف" : "Phone"}</span><span dir="ltr">{previewInvoice.customer_phone}</span></div>}
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">{lang === "ar" ? "الدفع" : "Payment"}</span><span>{formatPaymentLabel(previewInvoice)}</span></div>
              {previewInvoice.cancel_reason && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{lang === "ar" ? "سبب الإلغاء" : "Cancel reason"}</span><span className="text-rose-300">{previewInvoice.cancel_reason}</span></div>}
            </div>
            <div className="border-t border-[var(--border-subtle)] pt-3 mb-3">
              <div className="text-xs text-[var(--text-muted)] mb-2">{lang === "ar" ? "البنود" : "Items"}</div>
              {previewInvoice.items.map((it, i) => {
                const previewItemName = lang === "ar" ? (it.name_ar || it.name) : (it.name_en || it.name);
                return (
                <div key={i} className="py-1.5 border-b border-[var(--border-subtle)] last:border-0">
                  <div className="flex justify-between text-sm">
                    <div className="min-w-0 flex-1">
                      <div className="truncate" title={previewItemName}>{previewItemName}</div>
                      {it.qty > 1 && <div className="text-[10px] text-[var(--text-muted)]">{it.unit_price} × {it.qty}</div>}
                    </div>
                    <div className="ms-3 font-mono">{((it.unit_price || 0) * (it.qty || 1)).toFixed(2)}</div>
                  </div>
                  {(it.selected_services || []).map((sel) => (
                    <div key={sel.service_id} className="text-[10px] text-[var(--text-muted)] ps-2 mt-0.5 flex items-center gap-1">
                      <span>• {selectedServiceLabel(sel, lang)}</span>
                      {sel.status === "pending" && (
                        <span className="text-[9px] text-amber-700">
                          ({lang === "ar" ? "متابعة" : "Follow-up"}{sel.scheduled_date ? ` ${sel.scheduled_date}` : ""})
                        </span>
                      )}
                    </div>
                  ))}
                </div>
              );})}
            </div>
            <div className="space-y-1 text-sm border-t border-[var(--border-subtle)] pt-3">
              <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("subtotal")}</span><span>{Number(previewInvoice.subtotal || 0).toFixed(2)}</span></div>
              {previewInvoice.discount > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("discount")}</span><span>-{Number(previewInvoice.discount).toFixed(2)}</span></div>}
              {previewInvoice.coupon_discount > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{lang === "ar" ? "كوبون" : "Coupon"} ({previewInvoice.coupon_code})</span><span className="gold-text">-{Number(previewInvoice.coupon_discount).toFixed(2)}</span></div>}
              {previewInvoice.loyalty_value_applied > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("points_value")}</span><span>-{Number(previewInvoice.loyalty_value_applied).toFixed(2)}</span></div>}
              {previewInvoice.tax > 0 && <div className="flex justify-between"><span className="text-[var(--text-muted)]">{t("tax")}</span><span>{Number(previewInvoice.tax).toFixed(2)}</span></div>}
              <div className="flex justify-between font-heading text-xl pt-2 border-t border-[var(--border-subtle)]"><span>{t("total")}</span><span className="gold-text">{Number(previewInvoice.total).toFixed(2)}</span></div>
              {(previewInvoice.tip_total || 0) > 0 && (
                <div className="pt-2 space-y-1">
                  <div className="flex justify-between"><span>{t("tip")}</span><span className="gold-text num-display">+{Number(previewInvoice.tip_total).toFixed(2)}</span></div>
                  {(previewInvoice.tip_splits || []).map((ts, i) => (
                    <div key={i} className="flex justify-between text-xs text-[var(--text-muted)] ps-2">
                      <span>{staff.find((s) => s.user_id === ts.employee_user_id)?.name || ts.employee_user_id}</span>
                      <span className="num-display">{Number(ts.amount).toFixed(2)}</span>
                    </div>
                  ))}
                  <div className="flex justify-between font-medium border-t border-[var(--border-subtle)] pt-1">
                    <span>{t("amount_due")}</span>
                    <span className="num-display">{Number(previewInvoice.amount_collected || previewInvoice.total + previewInvoice.tip_total).toFixed(2)}</span>
                  </div>
                </div>
              )}
            </div>
            {previewInvoice.loyalty_earned > 0 && (
              <div className="text-sm rose-text mt-3">⭐ {t("loyalty_earned")}: {previewInvoice.loyalty_earned}</div>
            )}
            <button onClick={() => sendWA(previewInvoice)} data-testid="send-whatsapp-btn" className="btn-rose-outline w-full py-3 rounded-md flex items-center justify-center gap-2 mt-4">
              <MessageCircle size={16}/>{lang === "ar" ? "إرسال الفاتورة PDF عبر واتساب" : "Send PDF invoice via WhatsApp"}
            </button>
            <div className="grid grid-cols-3 gap-2 mt-4">
              <button onClick={() => printNow(previewInvoice, "pos")} data-testid="print-pos-btn" className="btn-gold py-2.5 rounded-md text-xs flex items-center justify-center gap-1">
                <Receipt size={14}/>{lang === "ar" ? "POS" : "POS"}
              </button>
              <button onClick={() => printNow(previewInvoice, "a4")} data-testid="print-a4-btn" className="btn-rose-outline py-2.5 rounded-md text-xs flex items-center justify-center gap-1">
                <FileText size={14}/>{lang === "ar" ? "A4" : "A4"}
              </button>
              <button onClick={() => previewPdf(previewInvoice)} data-testid="preview-pdf-btn" className="btn-rose-outline py-2.5 rounded-md text-xs flex items-center justify-center gap-1">
                <FileText size={14}/>PDF
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Line-discount editor — opens when user taps the Tag icon on a cart row */}
      {editingDiscountIdx !== null && cart[editingDiscountIdx] && (
        <LineDiscountEditor
          item={cart[editingDiscountIdx]}
          reasons={discountReasons}
          policy={discountPolicy}
          onApply={(patch) => setCart((c) => c.map((x, idx) => idx === editingDiscountIdx ? { ...x, ...patch } : x))}
          onClose={() => setEditingDiscountIdx(null)}
        />
      )}

      <PickPackageModal
        open={!!pickPackageTarget}
        pkg={pickPackageTarget?.pkg}
        services={services}
        staff={staff}
        lang={lang}
        requireStaff={staffPaySettings.require_staff_on_services !== false}
        trackConsumption
        selectionLocked={!!pickPackageTarget?.selectionLocked}
        initialSelections={pickPackageTarget?.initialSelections || []}
        confirmLabel={pickPackageTarget?.confirmLabel}
        onClose={() => {
          setPickPackageTarget(null);
          afterConsumptionRef.current = null;
        }}
        onConfirm={handlePickPackageConfirm}
      />

      <SplitPayModal
        open={splitPayOpen}
        onClose={() => setSplitPayOpen(false)}
        onConfirm={checkoutSplit}
        total={amountDue}
        paymentMethods={paymentMethods}
        accounts={accounts}
        defaultMethodId={method}
        defaultAccountId={accountId}
        lang={lang}
        submitting={checkoutBusy}
      />

      <TipSplitModal
        open={tipSplitOpen}
        onClose={() => setTipSplitOpen(false)}
        onConfirm={(splits) => { setTipSplits(splits); setTipSplitOpen(false); }}
        tipTotal={tipTotal}
        cart={cart}
        staff={staff}
        lang={lang}
      />

      {bookingImportPrompt && (
        <div className="fixed inset-0 z-50 bg-[#2B1C1C]/40 backdrop-blur-sm flex items-center justify-center p-4" onMouseDown={(e) => onBackdropMouseDown(e, () => setBookingImportPrompt(null))}>
          <div className="card-surface p-6 w-full max-w-md space-y-4" onMouseDown={onModalPanelMouseDown} data-testid="booking-import-modal">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-[var(--gold-tint)] text-[var(--gold)] flex items-center justify-center shrink-0">
                <CalendarCheck size={20}/>
              </div>
              <div className="min-w-0 flex-1">
                <h3 className="font-heading text-lg text-[var(--text)]">
                  {lang === "ar" ? "موعد غير مفوتر" : "Unbilled booking"}
                </h3>
                <p className="text-sm text-[var(--text-muted)] mt-1">
                  {lang === "ar"
                    ? `${bookingImportPrompt.custName} لديه ${bookingImportPrompt.statusLabel}`
                    : `${bookingImportPrompt.custName} has a ${bookingImportPrompt.statusLabel}`}
                </p>
              </div>
              <button onClick={() => setBookingImportPrompt(null)} className="text-[var(--text-muted)] hover:text-[var(--text)]"><X size={18}/></button>
            </div>
            <div className="rounded-md border border-[var(--border-subtle)] bg-[var(--bg-alt)] p-3 space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">{lang === "ar" ? "التاريخ" : "Date"}</span>
                <span className="font-medium num-display">{bookingImportPrompt.booking.date}{bookingImportPrompt.timeBit}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">{lang === "ar" ? "البنود" : "Items"}</span>
                <span>{bookingImportPrompt.booking.items.length}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-[var(--text-muted)]">{lang === "ar" ? "المجموع" : "Total"}</span>
                <span className="gold-text font-semibold num-display">{bookingImportPrompt.totalPrice.toFixed(2)}</span>
              </div>
              {bookingImportPrompt.otherCount > 0 && (
                <div className="text-xs text-[var(--text-muted)] pt-1 border-t border-[var(--border-subtle)]">
                  {lang === "ar"
                    ? `يوجد ${bookingImportPrompt.otherCount} موعد آخر غير مفوتر لهذا العميل`
                    : `${bookingImportPrompt.otherCount} more unbilled booking(s) for this customer`}
                </div>
              )}
            </div>
            <div className="max-h-36 overflow-auto space-y-1">
              {bookingImportPrompt.booking.items.map((it, i) => {
                const name = lang === "ar" ? (it.name_ar || it.name_en || it.name) : (it.name_en || it.name_ar || it.name);
                return (
                  <div key={i} className="flex justify-between text-xs py-1 border-b border-[var(--border-subtle)] last:border-0">
                    <span className="truncate pe-2">{name}</span>
                    <span className="num-display shrink-0">{(Number(it.price || 0) * Number(it.qty || 1)).toFixed(2)}</span>
                  </div>
                );
              })}
            </div>
            <div className="flex gap-2">
              <button type="button" onClick={() => setBookingImportPrompt(null)} className="flex-1 px-4 py-2 rounded-md border border-[var(--border)] text-sm">
                {lang === "ar" ? "ليس الآن" : "Not now"}
              </button>
              <button
                type="button"
                data-testid="booking-import-confirm"
                onClick={() => {
                  const { booking, statusLabel } = bookingImportPrompt;
                  setBookingImportPrompt(null);
                  applyBookingImport(booking, statusLabel);
                }}
                className="flex-1 px-4 py-2 rounded-md btn-gold text-sm font-semibold"
              >
                {lang === "ar" ? "فوترة الآن" : "Invoice now"}
              </button>
            </div>
          </div>
        </div>
      )}

      <ConfirmDialog
        open={!!waStubPrompt}
        title={lang === "ar" ? "واتساب غير مفعّل بالكامل" : "WhatsApp not fully configured"}
        description={waStubPrompt?.message}
        confirmLabel={lang === "ar" ? "فتح واتساب" : "Open WhatsApp"}
        cancelLabel={t("cancel")}
        variant="primary"
        testId="wa-stub-confirm"
        onCancel={() => setWaStubPrompt(null)}
        onConfirm={() => {
          if (!waStubPrompt) return;
          const { inv, pdfUrl } = waStubPrompt;
          const phone = (inv.customer_phone || "").replace(/[^0-9]/g, "");
          const body = lang === "ar"
            ? `فاتورة ${inv.invoice_number}\nالإجمالي: ${Number(inv.total).toFixed(2)} ${inv.currency || "QAR"}\nرابط الـPDF: ${pdfUrl}`
            : `Invoice ${inv.invoice_number}\nTotal: ${Number(inv.total).toFixed(2)} ${inv.currency || "QAR"}\nPDF: ${pdfUrl}`;
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(body)}`, "_blank");
          setWaStubPrompt(null);
        }}
      />
    </div>
  );
}
